import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Input,
  OnChanges,
  ViewChild,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { distinctUntilChanged, switchMap } from 'rxjs/operators';
import { GuestRoleService } from '~app/services/guest-role.service';
import { personIsGuest } from '~app/utilities';

/**
 * Subset of person fields the badge needs - works with slim, full, or the
 * structural shape carried on a `SelectOption.badgePerson`.
 */
export interface GuestBadgePerson {
  id?: string;
  /** Backend virtual: true when the person has any usda.gov email. */
  isUSDAUser?: boolean | null;
  /** Full-person email list, used when `isUSDAUser` isn't provided. */
  users?: { email?: string | null }[] | null;
}

export type GuestBadgeVariant = 'table' | 'dropdown' | 'profile';

/**
 * "G" badge shown next to guest user names.
 *
 * A person is a "guest" when they do NOT have a usda.gov email on their
 * profile. Resolution: an explicit `[guest]` boolean wins; otherwise if the
 * bound `[person]` carries an email signal (`isUSDAUser` or `users`) we decide
 * synchronously (people-table, crew grid, team lists); otherwise we look up the
 * person id via {@link GuestRoleService} (slim contexts: dropdowns,
 * trips/search).
 *
 * The component renders nothing when the resolved value is false, so hosts can
 * drop it in unconditionally beside any user name.
 *
 * Variants
 *  - `table`    18x18 circle, 12pt "G", tooltip="Guest" on hover/tap
 *  - `dropdown` 26x26 circle, 14pt "G", no tooltip (aria-label still present)
 *  - `profile`  Badge + "Guest account. Some features may be limited.";
 *               links to the Roles & Permissions help page with a "Learn More"
 *               tooltip on hover.
 */
@Component({
  selector: 'ciao-guest-badge',
  templateUrl: './guest-badge.component.html',
  styleUrls: ['./guest-badge.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, RouterModule, MatTooltipModule],
})
export class GuestBadgeComponent implements OnChanges {
  /** Visual variant; defaults to the table-size circle with hover tooltip. */
  @Input() variant: GuestBadgeVariant = 'table';

  /** Person object (slim or full). Preferred input. */
  @Input() person?: GuestBadgePerson | null;

  /** Person id fallback when no `person` is bound. */
  @Input() personId?: string | null;

  /** Explicit override that skips role resolution (use sparingly). */
  @Input() guest?: boolean | null;

  /** Roles & Permissions help center route (linked from the profile variant). */
  readonly helpLink = '/help/info/admin/roles-permissions';

  @ViewChild('tooltip') private tooltip?: MatTooltip;

  private readonly inputs$ = new BehaviorSubject<{
    guest?: boolean | null;
    person?: GuestBadgePerson | null;
    personId?: string | null;
  }>({});

  /** Whether to render the badge. */
  readonly isGuest$: Observable<boolean> = this.inputs$.pipe(
    distinctUntilChanged((a, b) => {
      return (
        a.guest === b.guest &&
        a.person === b.person &&
        a.personId === b.personId
      );
    }),
    switchMap(({ guest, person, personId }) => {
      if (typeof guest === 'boolean') {
        return of(guest);
      }
      if (person) {
        const fromObject = personIsGuest(person);
        if (typeof fromObject === 'boolean') {
          return of(fromObject);
        }
      }
      const id = person?.id ?? personId;
      if (!id) {
        return of(false);
      }
      return this.guestRoleService.isGuestPerson$(id);
    })
  );

  constructor(private guestRoleService: GuestRoleService) {}

  ngOnChanges(): void {
    this.inputs$.next({
      guest: this.guest,
      person: this.person,
      personId: this.personId,
    });
  }

  /**
   * Mobile: tap toggles the tooltip (AC requires tap-to-open, tap-to-close).
   * On desktop the hover behaviour is unchanged because MatTooltip handles it.
   *
   * Only touch clicks trigger the toggle + stopPropagation. Mouse clicks bubble
   * normally so the badge inside a clickable row (e.g. the team modal's member
   * list spans) doesn't swallow the row's click handler.
   */
  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    if (this.variant !== 'table' || !this.tooltip) {
      return;
    }
    // PointerEvent.pointerType distinguishes 'touch' from 'mouse'/'pen'. Click
    // events from touch interactions are dispatched as PointerEvents with
    // pointerType === 'touch'; mouse clicks come through as 'mouse'. Anything
    // we don't recognize falls through to "don't intercept."
    const pointerType = (event as PointerEvent).pointerType;
    if (pointerType !== 'touch') {
      return;
    }
    event.stopPropagation();
    this.tooltip.toggle();
  }
}
